import { useState, useEffect } from 'react';
import {
  Calendar, Plus, Pencil, Trash2, X, Save, Users, Clock, MapPin,
  Ticket, Star, DollarSign, AlertCircle, ChevronDown, ChevronUp
} from 'lucide-react';
import { api } from '../../lib/api';
import { formatPrice } from '../../lib/utils';

interface TicketType {
  id?: string;
  name: string;
  price: number;
  description?: string;
}

interface Event {
  id: string;
  name: string;
  slug: string;
  category: string;
  description?: string;
  event_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  location: string;
  capacity: number;
  spots_left: number;
  image_id?: string;
  featured: number;
  status: string;
  ticketTypes: TicketType[];
}

const EVENT_CATEGORIES = [
  'V8 Supercars',
  'Drag Racing',
  'Motorcycle',
  'Drifting',
  'Track Day',
  'Driver Training',
  'Corporate',
  'Other'
];

const EVENT_STATUS = [
  { value: 'upcoming', label: 'Upcoming', color: 'bg-blue-100 text-blue-700' },
  { value: 'on-sale', label: 'On Sale', color: 'bg-green-100 text-green-700' },
  { value: 'sold-out', label: 'Sold Out', color: 'bg-red-100 text-red-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-gray-100 text-gray-700' },
  { value: 'completed', label: 'Completed', color: 'bg-purple-100 text-purple-700' },
];

const DEFAULT_IMAGES: Record<string, string> = {
  'V8 Supercars': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80',
  'Drag Racing': 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=600&q=80',
  'Motorcycle': 'https://images.unsplash.com/photo-1558980664-769d59546b3d?w=600&q=80',
  'Drifting': 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600&q=80',
  'Track Day': 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&q=80',
  'Driver Training': 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&q=80',
  'Corporate': 'https://images.unsplash.com/photo-1493238792000-8113da705763?w=600&q=80',
  'Other': 'https://images.unsplash.com/photo-1518830246650-b54e3b0de5e0?w=600&q=80',
};

export function AdminEventsPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: 'Track Day',
    description: '',
    eventDate: '',
    endDate: '',
    startTime: '09:00',
    endTime: '17:00',
    location: 'DriveIT NQ Main Circuit',
    capacity: 1000,
    imageUrl: '',
    featured: false,
    status: 'upcoming',
  });
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([
    { name: 'General Admission', price: 5000, description: '' }
  ]);

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    try {
      setLoading(true);
      const res = await api.get('/api/admin/events');
      if (res.success) {
        setEvents(res.data);
      }
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setLoading(false);
    }
  }

  function openCreateForm() {
    setEditingEvent(null);
    setFormData({
      name: '',
      category: 'Track Day',
      description: '',
      eventDate: '',
      endDate: '',
      startTime: '09:00',
      endTime: '17:00',
      location: 'DriveIT NQ Main Circuit',
      capacity: 1000,
      imageUrl: '',
      featured: false,
      status: 'upcoming',
    });
    setTicketTypes([{ name: 'General Admission', price: 5000, description: '' }]);
    setShowForm(true);
    setError(null);
  }

  function openEditForm(event: Event) {
    setEditingEvent(event);
    setFormData({
      name: event.name,
      category: event.category,
      description: event.description || '',
      eventDate: event.event_date,
      endDate: event.end_date || '',
      startTime: event.start_time || '09:00',
      endTime: event.end_time || '17:00',
      location: event.location,
      capacity: event.capacity,
      imageUrl: event.image_id || '',
      featured: event.featured === 1,
      status: event.status,
    });
    setTicketTypes(event.ticketTypes.length > 0 ? event.ticketTypes : [{ name: 'General Admission', price: 5000, description: '' }]);
    setShowForm(true);
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!formData.name || !formData.eventDate || !formData.capacity) {
      setError('Please fill in all required fields');
      return;
    }

    if (ticketTypes.length === 0 || ticketTypes.some(t => !t.name || t.price <= 0)) {
      setError('Please add at least one valid ticket type');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: formData.name,
        category: formData.category,
        description: formData.description || null,
        eventDate: formData.eventDate,
        endDate: formData.endDate || null,
        startTime: formData.startTime,
        endTime: formData.endTime,
        location: formData.location,
        capacity: formData.capacity,
        imageUrl: formData.imageUrl || DEFAULT_IMAGES[formData.category] || null,
        featured: formData.featured,
        status: formData.status,
        ticketTypes: ticketTypes.map(t => ({
          name: t.name,
          price: t.price,
          description: t.description || null,
        })),
      };

      let res;
      if (editingEvent) {
        res = await api.patch(`/api/admin/events/${editingEvent.id}`, payload);
      } else {
        res = await api.post('/api/admin/events', payload);
      }

      if (res.success) {
        await loadEvents();
        setShowForm(false);
      } else {
        setError(res.error || 'Failed to save event');
      }
    } catch (err) {
      setError('Failed to save event');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(event: Event) {
    if (!confirm(`Are you sure you want to delete "${event.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const res = await api.delete(`/api/admin/events/${event.id}`);
      if (res.success) {
        await loadEvents();
      }
    } catch (err) {
      console.error('Failed to delete event:', err);
    }
  }

  function addTicketType() {
    setTicketTypes([...ticketTypes, { name: '', price: 0, description: '' }]);
  }

  function updateTicketType(index: number, field: keyof TicketType, value: string | number) {
    setTicketTypes(ticketTypes.map((t, i) =>
      i === index ? { ...t, [field]: value } : t
    ));
  }

  function removeTicketType(index: number) {
    if (ticketTypes.length > 1) {
      setTicketTypes(ticketTypes.filter((_, i) => i !== index));
    }
  }

  function getStatusBadge(status: string) {
    const s = EVENT_STATUS.find(es => es.value === status);
    return s ? s.color : 'bg-gray-100 text-gray-700';
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events Management</h1>
          <p className="text-gray-600">Create and manage events with ticket types</p>
        </div>
        <button onClick={openCreateForm} className="btn btn-primary">
          <Plus size={18} className="mr-2" />
          Add Event
        </button>
      </div>

      {/* Events List */}
      {events.length === 0 ? (
        <div className="card p-12 text-center">
          <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Events Yet</h3>
          <p className="text-gray-600 mb-4">Create your first event to start selling tickets</p>
          <button onClick={openCreateForm} className="btn btn-primary">
            <Plus size={18} className="mr-2" />
            Create Event
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <div key={event.id} className="card overflow-hidden">
              {/* Event Header */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)}
              >
                <div className="flex items-start gap-4">
                  {/* Image */}
                  <div className="w-24 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                    <img
                      src={event.image_id || DEFAULT_IMAGES[event.category] || DEFAULT_IMAGES.Other}
                      alt={event.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-900 truncate">{event.name}</h3>
                      {event.featured === 1 && (
                        <Star size={16} className="text-yellow-500 fill-yellow-500 flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(event.event_date).toLocaleDateString('en-AU', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {event.start_time || '09:00'}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {event.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {event.spots_left}/{event.capacity}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                        {event.category}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(event.status)}`}>
                        {EVENT_STATUS.find(s => s.value === event.status)?.label || event.status}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Ticket size={12} />
                        {event.ticketTypes.length} ticket type{event.ticketTypes.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditForm(event); }}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(event); }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                    {expandedEvent === event.id ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedEvent === event.id && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  {event.description && (
                    <p className="text-gray-600 text-sm mb-4">{event.description}</p>
                  )}
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Ticket size={16} />
                    Ticket Types
                  </h4>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {event.ticketTypes.map((ticket, i) => (
                      <div key={i} className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-start">
                          <span className="font-medium text-gray-900">{ticket.name}</span>
                          <span className="font-bold text-green-600">{formatPrice(ticket.price)}</span>
                        </div>
                        {ticket.description && (
                          <p className="text-xs text-gray-500 mt-1">{ticket.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
                <h2 className="text-lg font-bold text-gray-900">
                  {editingEvent ? 'Edit Event' : 'Create New Event'}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-4 space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
                    <AlertCircle size={18} />
                    {error}
                  </div>
                )}

                {/* Basic Info */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Event Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input w-full"
                      placeholder="e.g., Townsville 500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="input w-full"
                    >
                      {EVENT_CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="input w-full"
                    >
                      {EVENT_STATUS.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="input w-full h-20 resize-none"
                      placeholder="Event description..."
                    />
                  </div>
                </div>

                {/* Date & Time */}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Calendar size={18} />
                    Date & Time
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Event Date *
                      </label>
                      <input
                        type="date"
                        value={formData.eventDate}
                        onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                        className="input w-full"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date (multi-day)
                      </label>
                      <input
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={formData.startTime}
                        onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={formData.endTime}
                        onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                        className="input w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Venue & Capacity */}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin size={18} />
                    Venue & Capacity
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location
                      </label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="input w-full"
                        placeholder="e.g., Main Circuit"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Capacity *
                      </label>
                      <input
                        type="number"
                        value={formData.capacity}
                        onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                        className="input w-full"
                        min="1"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Image & Featured */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Image URL (optional)
                      </label>
                      <input
                        type="url"
                        value={formData.imageUrl}
                        onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                        className="input w-full"
                        placeholder="https://..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty for category default</p>
                    </div>
                    <div className="flex items-center">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.featured}
                          onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                          className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                        />
                        <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                          <Star size={16} className="text-yellow-500" />
                          Featured Event
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Ticket Types */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Ticket size={18} />
                      Ticket Types
                    </h3>
                    <button
                      type="button"
                      onClick={addTicketType}
                      className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                    >
                      <Plus size={16} />
                      Add Ticket Type
                    </button>
                  </div>
                  <div className="space-y-3">
                    {ticketTypes.map((ticket, index) => (
                      <div key={index} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={ticket.name}
                            onChange={(e) => updateTicketType(index, 'name', e.target.value)}
                            className="input w-full mb-2"
                            placeholder="Ticket name (e.g., General Admission)"
                            required
                          />
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="number"
                                value={ticket.price / 100}
                                onChange={(e) => updateTicketType(index, 'price', Math.round(parseFloat(e.target.value || '0') * 100))}
                                className="input w-full pl-8"
                                placeholder="Price"
                                min="0"
                                step="0.01"
                                required
                              />
                            </div>
                            <input
                              type="text"
                              value={ticket.description || ''}
                              onChange={(e) => updateTicketType(index, 'description', e.target.value)}
                              className="input flex-1"
                              placeholder="Description (optional)"
                            />
                          </div>
                        </div>
                        {ticketTypes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTicketType(index)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  <Save size={18} className="mr-2" />
                  {saving ? 'Saving...' : editingEvent ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
